import * as React from 'react';
import './listPageStyles';

/**
 * Cursor-based pagination for the connection-style screens.
 *
 * Was a wrapper around sha-el-design's Pagination with `totalCount={Infinity}`
 * (PROJECT-ANALYSIS BUG-13), which made the Next control render enabled on the
 * last page forever. The click was already a no-op -- onPageChange guards on
 * hasNextPage -- so the control simply lied: it looked available and did
 * nothing. `hasNextPage` was passed in all along and is now actually used.
 *
 * It also rendered a visually different pager from the offset-based one on the
 * work lists (section 9: "Two parallel pagination implementations ... with
 * different visual treatment"). Both now share .cs-list__pager.
 *
 * Props are unchanged, so every existing call site keeps working.
 */
export class Pagination extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      currentPage: 1,
      after: '',
    };
  }

  afterStack = [''];

  next = () => {
    if (!this.props.hasNextPage) {
      return;
    }
    const after = this.props.endCursor;
    // In-flight guard: while the next page is loading, the query component
    // keeps rendering the PREVIOUS response, so endCursor is still the one we
    // just used. Without this, a second click pushed the same cursor again and
    // advanced the counter to a page that was never fetched -- Prev then had
    // to be clicked twice to move the data.
    if (after === this.afterStack[this.afterStack.length - 1]) {
      return;
    }
    this.afterStack.push(after);
    this.setState({ after, currentPage: this.state.currentPage + 1 });
    this.props.after(after);
  }

  prev = () => {
    if (this.afterStack.length <= 1) {
      return;
    }
    this.afterStack.pop();
    const after = this.afterStack[this.afterStack.length - 1];
    this.setState({ after, currentPage: this.state.currentPage - 1 });
    this.props.after(after);
  }

  render() {
    const { currentPage } = this.state;
    return (
      <div className='cs-list__pager'>
        <span className='cs-list__pager-info' />
        <button
          type='button'
          className='cs-list__page-btn'
          onClick={this.prev}
          disabled={currentPage === 1}
        >
          Prev
        </button>
        <span className='cs-list__page-current'>{currentPage}</span>
        <button
          type='button'
          className='cs-list__page-btn'
          onClick={this.next}
          disabled={!this.props.hasNextPage}
        >
          Next
        </button>
      </div>
    );
  }
}

interface State {
  currentPage: number;
  after: string;
}

interface Props {
  endCursor: string;
  hasNextPage: boolean;
  after: (after: string) => void;
}
